type AnalyticsCapture = {
  capture?: (event: string, properties?: Record<string, unknown>) => void;
} | null | undefined;

type InviteDiagnosticEvent = {
  event: string;
  at: string;
  properties: Record<string, unknown>;
};

const STORAGE_KEY = "invite-flow-diagnostics";
const MAX_EVENTS = 50;
const TTL_MS = 1000 * 60 * 60 * 2;

type StoredInviteDiagnostics = {
  createdAt: number;
  events: InviteDiagnosticEvent[];
};

const getStorage = (): Storage => window.sessionStorage;

const readEvents = (): InviteDiagnosticEvent[] => {
  try {
    const raw = getStorage().getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as StoredInviteDiagnostics;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      typeof parsed.createdAt !== "number" ||
      !Array.isArray(parsed.events)
    ) {
      getStorage().removeItem(STORAGE_KEY);
      return [];
    }

    if (Date.now() - parsed.createdAt > TTL_MS) {
      getStorage().removeItem(STORAGE_KEY);
      return [];
    }

    return parsed.events;
  } catch {
    return [];
  }
};

const writeEvents = (events: InviteDiagnosticEvent[]): void => {
  const payload: StoredInviteDiagnostics = {
    createdAt: Date.now(),
    events: events.slice(-MAX_EVENTS),
  };
  getStorage().setItem(STORAGE_KEY, JSON.stringify(payload));
};

export const recordInviteDiagnostic = (
  analytics: AnalyticsCapture,
  event: string,
  properties: Record<string, unknown> = {},
): void => {
  const payload = {
    ...properties,
    path: window.location.pathname,
    search: window.location.search,
    userAgent: navigator.userAgent,
  };

  try {
    const nextEvent: InviteDiagnosticEvent = {
      event,
      at: new Date().toISOString(),
      properties: payload,
    };
    writeEvents([...readEvents(), nextEvent]);
  } catch {
    // Ignore diagnostics persistence errors.
  }

  analytics?.capture?.(event, payload);
};

export const clearInviteDiagnostics = (): void => {
  getStorage().removeItem(STORAGE_KEY);
};
